export type Lang = "en" | "hy";

const en = {
  lang: "🇬🇧 EN",
  setup: {
    tagline:
      "Build wealth, make smart choices, and learn real finance — for the whole family.",
    players: "👥 Players (2–6)",
    playerPlaceholder: (n: number) => `Player ${n}`,
    addPlayer: "+ Add player",
    gameLength: "⏱ Game Length",
    gameLengths: [
      { label: "Quick", time: "~20 min" },
      { label: "Standard", time: "~30 min" },
      { label: "Long", time: "~45 min" },
    ],
    rounds: "rounds",
    startGame: "Start Game →",
    footer1: "Earn, invest, prepare — most wealth wins!",
    footer2: "Watch your Work-Life Balance or burn out.",
  },
  board: {
    newGame: "New Game",
    players: "PLAYERS",
    rankings: "RANKINGS",
    round: "Round",
    currentTurn: "Current Turn",
    costOfLiving: "Cost of living:",
    perTurn: "/ turn",
  },
  phases: {
    DRAW_EVENT: "⚡ Draw Event Card",
    CHOOSE_DECK: "🃏 Choose Your Path",
    REVEAL_CARD: "👁 Card Revealed",
    INVEST: "📈 Investment Phase",
    SPEND: "💸 Pay Expenses",
    BUY_ITEMS: "🛒 Buy Items",
    END_TURN: "⏭ End Turn",
    GAME_OVER: "🏆 Game Over",
  },
  action: {
    timeForEvent: "Time for an Event!",
    eventDesc: "Draw an event card to see what life throws at you this round.",
    okContinue: "OK, continue →",
    drawEventCard: "⚡ Draw Event Card",
    choosePath: "Choose Your Path",
    whatWillYouDo: "What will you do this turn?",
    earningsDeck: "💰 Earnings Deck",
    earningsDesc: "Earn money now. More if you have matching skills.",
    prepDeck: "📚 Preparation Deck",
    prepDesc: "Gain skill icons. No money now, but big boosts later.",
    rest: "😴 Rest This Turn",
    restDesc: "Skip earning, restore +2 WLB",
    nextInvest: "Next: Invest →",
    investments: "📈 Investments",
    invest: "💼 Invest",
    portfolio: "📊 Portfolio",
    skip: "⏭ Skip",
    lowRisk: "🏦 Low Risk",
    highRisk: "🚀 High Risk",
    lowRiskDesc: "Steady +5%/round. Outcomes: -10% to +25%",
    highRiskDesc: "Volatile +10%/round. Outcomes: -60% to +100%",
    amount: "Amount",
    available: (n: number) => `Available: 🪙${n}`,
    investBtn: (n: number) => `Invest 🪙${n} →`,
    investingIn: "Investing",
    inFund: (risk: "low" | "high" | "bond") =>
      risk === "bond"
        ? "Government Bond"
        : risk === "low"
          ? "Index Fund"
          : "Tech Startup",
    rollDice: "🎲 Roll the Dice",
    rolling: "Rolling...",
    continueTo: "Continue →",
    noInvestments: "No investments yet",
    doneInvestments: "Done with investments →",
    sell: "Sell",
    paidNowRound: (paid: number, now: number, inc: number) =>
      `Paid 🪙${paid} · Now 🪙${now} · +${inc}/round`,
    burnoutTitle: "is Burnt Out!",
    burnoutDesc: "Skipping this turn to recover. WLB will restore to 5.",
    recoverPassTurn: "Recover & pass turn →",
    bondLabel: "🏛️ Bond",
    bondDesc: "Fixed return, no risk",
    bondPortfolio: (n: number) => `🏛️ Fixed · +${n}/round · sell at cost`,
    perRound: "/round",
    yourTurn: "Your Turn!",
    diceJackpot: "Jackpot! 🎉",
    diceBigWin: "Big Win! 🚀",
    diceNiceGain: "Nice Gain ✨",
    diceBreakEven: "Break Even 😐",
    diceSmallLoss: "Small Loss 😬",
    diceBigLoss: "Ouch! Big Loss 💸",
  },
  spend: {
    costOfLivingTitle: "💸 Cost of Living",
    costOfLivingNote:
      "This is deducted automatically — it's the cost of being alive!",
    yourBalance: (before: number, after: number) =>
      `Your balance: 🪙${before} → 🪙${Math.max(0, after)}`,
    payAndContinue: (n: number) => `Pay 🪙${n} and continue →`,
    buyItemsTitle: "🛒 Optional: Buy items to improve your life or WLB",
    cantAfford: "Nothing you can afford right now",
    endTurn: "End Turn →",
    youOwn: "You own:",
    wlbBonus: (n: number) => `+${n} WLB`,
    wlbBoostHint: "✨ Boost your WLB",
    sellBackTitle: "Your items — sell back",
    sellingRemovesWlb: (n: number) => `selling removes -${n} WLB`,
    sellFor: (n: number) => `Sell 🪙${n}`,
    popularBadge: "🔥 Popular",
    resell: (n: number) => `resell 🪙${n}`,
  },
  items: {
    item_001: {
      name: "Family Holiday",
      description: "A trip together refreshes everyone.",
    },
    item_002: {
      name: "Bicycle",
      description: "Get fit and save on transport.",
    },
    item_003: {
      name: "Gaming Console",
      description: "Fun for the whole family.",
    },
    item_004: {
      name: "Reliable Car",
      description: "Opens up better jobs and saves commute stress.",
    },
    item_005: {
      name: "Garden Shed",
      description: "A weekend project that adds home value.",
    },
    item_006: {
      name: "Music Lessons",
      description: "A creative outlet that reduces stress.",
    },
    item_007: {
      name: "Emergency Fund Jar",
      description: "Locked savings — protected from events.",
    },
    item_008: {
      name: "Fitness Equipment",
      description: "Exercise improves focus and mood.",
    },
  } as Record<string, { name: string; description: string }>,
  events: {
    event_001: {
      title: "Medical Bill",
      description: "An unexpected health expense hits.",
      conceptExplanation:
        "Emergency funds exist for exactly this. Keeping 3–6 months of expenses saved protects you from a debt spiral when life surprises you.",
    },
    event_002: {
      title: "Year-End Bonus",
      description: "Your employer rewards your performance.",
      conceptExplanation:
        "Bonuses are variable income — great when they come, but never count on them for fixed expenses. Save or invest a bonus rather than spending it immediately.",
    },
    event_003: {
      title: "Global Recession",
      description: "Markets crash. Everyone feels it.",
      conceptExplanation:
        "Recessions affect all asset classes. Diversification — holding different types of investments — reduces the blow. Cash savings provide a buffer.",
    },
    event_004: {
      title: "Paid Family Leave",
      description: "Take time with your family — paid.",
      conceptExplanation:
        "Countries and companies with paid leave help workers maintain income without sacrificing family time. When evaluating a job, benefits matter as much as salary.",
    },
    event_005: {
      title: "Car Breakdown",
      description: "Your engine needs urgent repair.",
      conceptExplanation:
        "Sinking funds — money set aside monthly for predictable irregular costs like car repairs — prevent budget disruption. Plan for irregular expenses before they happen.",
    },
    event_006: {
      title: "Tax Refund",
      description: "The government sends money back!",
      conceptExplanation:
        "A tax refund means you overpaid during the year — it's your own money returned, not a windfall. Smart tax planning can improve your cash flow all year round.",
    },
    event_007: {
      title: "Rent Increase",
      description: "Your landlord raises rent by 15%.",
      conceptExplanation:
        "Renting means your housing cost is controlled by someone else. Owning a home locks in your payment. Understanding rent vs. buy trade-offs is a core financial skill.",
    },
    event_008: {
      title: "Bull Market",
      description: "Stock prices soar across the board!",
      conceptExplanation:
        "A bull market rewards investors who stayed in when things looked uncertain. Time in the market beats timing the market — patience is an investor's superpower.",
    },
    event_009: {
      title: "Job Layoff",
      description: "Your employer downsizes. You're let go.",
      conceptExplanation:
        "Even hardworking people get laid off. An emergency fund of 3–6 months of expenses buys you time to find the right next job rather than the first available one.",
    },
    event_010: {
      title: "Side Hustle Takes Off",
      description: "Your extra project brings in real money.",
      conceptExplanation:
        "Multiple income streams protect you. If one dries up, the others keep flowing. Starting a side hustle early — even small — builds this resilience.",
    },
    event_011: {
      title: "Unexpected Bill",
      description: "Home appliance breaks down.",
      conceptExplanation:
        "Homeownership comes with maintenance costs averaging 1–2% of home value per year. Budgeting for this prevents emergencies from becoming financial crises.",
    },
    event_012: {
      title: "Birthday Gift!",
      description: "Family sends you birthday money.",
      conceptExplanation:
        "A windfall — unexpected money — is a chance to invest rather than spend. Even a small investment compounded over 10 years can grow significantly.",
    },
    event_013: {
      title: "Market Correction",
      description: "Investments lose 10% of their value.",
      conceptExplanation:
        "Market corrections are normal — they happen roughly once a year. Long-term investors don't panic-sell. Riding out corrections is how wealth is built.",
    },
    event_014: {
      title: "Scholarship Award",
      description: "You win a surprise education grant.",
      conceptExplanation:
        "Scholarships and grants are free money for education — unlike student loans they don't need to be repaid. Applying for them is always worth the time.",
    },
    event_015: {
      title: "Cost of Living Rise",
      description: "Inflation hits — everything costs more.",
      conceptExplanation:
        "Inflation erodes the value of cash sitting idle. Money invested in assets that grow faster than inflation — like stocks — protects your purchasing power over time.",
    },
    event_016: {
      title: "Company Profit Share",
      description: "Your employer shares the success.",
      conceptExplanation:
        "Profit-sharing and equity compensation are powerful benefits that build wealth over time. When evaluating a job offer, look beyond the salary to the total package.",
    },
    event_017: {
      title: "Water Pipe Burst",
      description: "A plumbing disaster at home.",
      conceptExplanation:
        "Homeownership means you're the one paying for repairs. Home insurance covers big disasters, but you still pay deductibles. Always keep a home emergency fund.",
    },
    event_018: {
      title: "Interest Rate Rise",
      description: "The central bank raises rates. Debt costs more.",
      conceptExplanation:
        "Central banks raise interest rates to slow inflation. This increases loan and mortgage costs but also means savings accounts pay more. Fixed-rate debt protects you.",
    },
    event_019: {
      title: "Friend's Wedding",
      description: "You're in the wedding party. The fun is expensive.",
      conceptExplanation:
        "Social spending is real spending. Being a good friend doesn't mean going into debt. It's okay to set a budget for social events and communicate it honestly.",
    },
    event_020: {
      title: "Viral Social Post",
      description: "Something you made blew up online.",
      conceptExplanation:
        "Virality can create real income — brand deals, merchandise, speaking invites. But it's unpredictable. Never rely on it as a primary income source.",
    },
    event_021: {
      title: "Tech Boom",
      description: "Innovation drives the whole economy up.",
      conceptExplanation:
        "Sectors lead economic cycles. When tech booms, investors with exposure win big. Diversifying into growth sectors — even a small amount — builds long-term wealth.",
    },
    event_022: {
      title: "Identity Theft",
      description: "Someone used your financial details.",
      conceptExplanation:
        "Cybercrime costs billions yearly. Protecting your digital identity — strong passwords, two-factor auth, credit freezes — is as important as locking your front door.",
    },
    event_023: {
      title: "Free Online Course",
      description: "You completed a free certificate course.",
      conceptExplanation:
        "Free learning platforms like Coursera, edX, and Khan Academy have democratised education. A free course won't replace a degree, but it can demonstrate motivation and build skills.",
    },
    event_024: {
      title: "Energy Bill Spike",
      description: "Winter hits hard. Heating costs soar.",
      conceptExplanation:
        "Fixed costs like utilities vary more than people expect. A home energy audit, better insulation, or solar panels are investments that reduce future living costs.",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  earnings: {
    earn_001: {
      title: "Freelance Web Project",
      description: "A client pays you to build their website.",
      conceptExplanation:
        "Freelancing lets you sell skills directly. Having a portfolio (degree or skill badge) lets you charge premium rates — employers and clients pay more for proven expertise.",
    },
    earn_002: {
      title: "Overtime Shift",
      description: "Your boss needs extra hours covered.",
      conceptExplanation:
        "Overtime pays more per hour but costs energy. Overworking long-term leads to burnout — watch your Work-Life Balance! More money isn't always worth it.",
    },
    earn_003: {
      title: "Skilled Trades Job",
      description: "A plumbing contract comes in.",
      conceptExplanation:
        "Trade certifications qualify you for high-demand jobs that pay well without a four-year degree. Electricians, plumbers, and welders are always in demand.",
    },
    earn_004: {
      title: "Team Lead Promotion",
      description: "Your leadership got noticed.",
      conceptExplanation:
        "Leadership skills are often worth more than technical ability alone. Senior roles with management responsibilities command higher salaries and benefits.",
    },
    earn_005: {
      title: "Language Tutoring",
      description: "Teach your second language online.",
      conceptExplanation:
        "Speaking a second language opens doors to global clients and international roles. Bilingual workers earn on average 10–15% more than their monolingual peers.",
    },
    earn_006: {
      title: "Entry-Level Office Job",
      description: "A steady paycheck every month.",
      conceptExplanation:
        "Steady income is the foundation of financial stability. Even a modest regular income lets you budget, save, and invest over time.",
    },
    earn_007: {
      title: "Consulting Gig",
      description: "A company needs your expertise.",
      conceptExplanation:
        "Consultants charge for specialized knowledge. The more rare your skills, the more you can charge. Networking often brings these opportunities.",
    },
    earn_008: {
      title: "Weekend Market Stall",
      description: "Sell handmade goods at the local market.",
      conceptExplanation:
        "A side hustle can supplement your main income. Small businesses often start this way — testing an idea before going full-time.",
    },
    earn_009: {
      title: "Software Engineer Role",
      description: "A tech company hires you full-time.",
      conceptExplanation:
        "Technical roles in software command some of the highest salaries in the job market. A skill badge or degree dramatically improves your chances of landing them.",
    },
    earn_010: {
      title: "Delivery Driver",
      description: "Flexible hours, instant pay.",
      conceptExplanation:
        "Gig work offers flexibility but no benefits like health insurance or paid leave. It's great short-term but consider what you're giving up long-term.",
    },
    earn_011: {
      title: "International Sales Deal",
      description: "Close a deal with an overseas client.",
      conceptExplanation:
        "Global business skills are increasingly valuable. Companies pay premiums for employees who can bridge cultural and language gaps to close deals abroad.",
    },
    earn_012: {
      title: "Lawn Mowing Round",
      description: "Help neighbours with their gardens.",
      conceptExplanation:
        "A great first job for kids! Trading your time for money is the simplest form of earning. As you build skills, you can charge more and work smarter.",
    },
    earn_013: {
      title: "Skilled Electrician Call-Out",
      description: "Emergency electrical repair job.",
      conceptExplanation:
        "Emergency trades workers earn premium rates because they're available when others aren't. A trade cert plus availability is a powerful income combination.",
    },
    earn_014: {
      title: "Referral Bonus",
      description: "You recommended a colleague — score!",
      conceptExplanation:
        "A strong network pays off in unexpected ways. Many companies offer referral bonuses because they trust employees to recommend quality candidates.",
    },
    earn_015: {
      title: "Research Grant",
      description: "Awarded funding for your research project.",
      conceptExplanation:
        "Advanced degrees open doors to research funding, grants, and fellowships — income sources that don't exist without the credential.",
    },
    earn_016: {
      title: "Babysitting",
      description: "Look after the kids next door.",
      conceptExplanation:
        "Responsibility earns trust — and trust earns money. Reliable babysitters get repeat clients and referrals, building a small business from a simple service.",
    },
    earn_017: {
      title: "Podcast Sponsorship",
      description: "Your podcast landed a paid sponsor.",
      conceptExplanation:
        "Content creation is a modern income stream. Building an audience first takes time, but once you have it, sponsors pay for access to your listeners.",
    },
    earn_018: {
      title: "Real Estate Showing",
      description: "Help a family find their dream home.",
      conceptExplanation:
        "Real estate agents earn commission — a percentage of the sale price. A single deal can equal months of salary. Commission-based work has high highs and unpredictable lows.",
    },
    earn_019: {
      title: "Stock Photo Sale",
      description: "Your photos sold on a stock platform.",
      conceptExplanation:
        "Royalty income means you earn every time someone uses your creation — without more work from you. This is one of the most powerful forms of passive income.",
    },
    earn_020: {
      title: "Tutoring Session",
      description: "Help a student pass their exam.",
      conceptExplanation:
        "Teaching what you know is one of the oldest ways to earn. The more qualified you are, the more you can charge — and online platforms let tutors reach students worldwide.",
    },
    earn_021: {
      title: "Catering Gig",
      description: "Cook and serve at a private event.",
      conceptExplanation:
        "Food businesses have low barriers to entry. A catering side hustle can grow into a full business — many restaurant owners started by cooking for friends and events.",
    },
    earn_022: {
      title: "App Development Contract",
      description: "A startup hires you to build their MVP.",
      conceptExplanation:
        "Contract software work pays premium rates because it's project-based, not salaried. Developers with a portfolio can charge day rates that equal weeks of office pay.",
    },
    earn_023: {
      title: "Translation Work",
      description: "Translate a business document.",
      conceptExplanation:
        "Professional translators earn significantly more than casual interpreters. Specialising in legal, medical or technical translation commands premium rates.",
    },
    earn_024: {
      title: "Vintage Resale",
      description: "Flip thrift-store finds online.",
      conceptExplanation:
        "Buy low, sell high — the core of trading. Resellers learn to spot undervalued items and sell where demand is highest. This skill applies equally to stocks, real estate and businesses.",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  preparation: {
    prep_001: {
      title: "Community College Degree",
      description: "Two years of focused study.",
      conceptExplanation:
        "A two-year degree costs less than a four-year university but still unlocks higher-paying jobs. Return on investment can be faster — you enter the workforce sooner with less debt.",
    },
    prep_002: {
      title: "Electrician Certification",
      description: "Complete a trade apprenticeship.",
      conceptExplanation:
        "Trade certifications are in high demand. Electricians earn median salaries above many college graduates — with no student debt and immediate employment.",
    },
    prep_003: {
      title: "Online Coding Bootcamp",
      description: "12 weeks of intensive learning.",
      conceptExplanation:
        "Bootcamps are fast and cheaper than degrees. They grant a skill badge but not a full degree — some jobs require both. A great way to change careers quickly.",
    },
    prep_004: {
      title: "Networking Event",
      description: "Meet people in your industry.",
      conceptExplanation:
        "A strong professional network can multiply income. Many jobs are filled through connections, not job boards. Who you know is as important as what you know.",
    },
    prep_005: {
      title: "Spanish Language Course",
      description: "Learn to speak a second language.",
      conceptExplanation:
        "Bilingual workers earn 10–15% more on average. Speaking Spanish opens doors across Latin America, the US, and Europe — a hugely valuable career skill.",
    },
    prep_006: {
      title: "Leadership Workshop",
      description: "Build management and team skills.",
      conceptExplanation:
        "Leadership skills are consistently among the highest-valued by employers. Managers and team leads earn significantly more than individual contributors in most fields.",
    },
    prep_007: {
      title: "University Degree",
      description: "Four years of higher education.",
      conceptExplanation:
        "A four-year university degree opens doors to professional roles, postgraduate study, and research careers. The long-term income premium is significant — but so is the upfront cost.",
    },
    prep_008: {
      title: "Plumbing Apprenticeship",
      description: "Learn on the job with a master plumber.",
      conceptExplanation:
        "Apprenticeships let you earn while you learn — no student debt. Plumbing is recession-proof work because pipes always need fixing!",
    },
    prep_009: {
      title: "Design Skills Course",
      description: "Learn graphic design fundamentals.",
      conceptExplanation:
        "Creative skills like design are in demand across every industry. A skill badge for design can combine with other qualifications to open freelance and full-time roles.",
    },
    prep_010: {
      title: "Industry Conference",
      description: "Attend and present at your field's big event.",
      conceptExplanation:
        "Conferences combine learning with networking. Speaking at one positions you as an expert and generates connections that can lead to jobs, clients, and partnerships.",
    },
    prep_011: {
      title: "French Immersion Program",
      description: "Three months abroad in Paris.",
      conceptExplanation:
        "Language immersion is the fastest way to fluency. Speaking French opens doors to Europe, Canada, and francophone Africa — markets with enormous economic activity.",
    },
    prep_012: {
      title: "Data Science Certificate",
      description: "Learn to analyse and interpret data.",
      conceptExplanation:
        "Data literacy is one of the fastest-growing skills employers need. A data science credential commands a salary premium across almost every industry.",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  card: {
    earningsLabel: "EARNINGS",
    prepLabel: "PREPARATION",
    eventLabel: "EVENT",
    boostedBy: "Boosted by:",
    grants: "Grants:",
    bonus: "bonus",
    affectsAll: "⚠️ Affects ALL players",
    halvespenalty: "halves the penalty",
    wlbCost: (n: number) => `WLB -${n}`,
    wlbChange: (n: number) => `WLB ${n > 0 ? "+" : ""}${n}`,
  },
  icons: {
    degree: "🎓 Degree",
    trade_cert: "🔧 Trade Cert",
    skill_badge: "💻 Skill",
    network: "🤝 Network",
    language: "🌍 Language",
    leadership: "⭐ Leadership",
  },
  modal: {
    financeTip: "💡 FINANCE TIP",
    gotIt: "Got it — Continue →",
    burnoutTitle: "Burnout!",
    burnoutMsg: (name: string) =>
      `${name} worked too hard and burned out. They'll skip their next turn and recover some WLB.`,
    burnoutLesson:
      "Burnout teaches us that rest isn't optional — it's a financial strategy too. Burnout costs more than the income you gained.",
    understood: "Understood →",
  },
  player: {
    wlb: "WLB",
    netWorth: "Net Worth",
    burnoutBadge: "💤 Burnout",
    investments: (n: number) => `📈 ${n} investment${n > 1 ? "s" : ""}`,
    passiveIncome: (n: number) => `+${n}🪙/round`,
  },
  endgame: {
    gameOver: "Game Over!",
    wins: (name: string, nw: number) => `${name} wins with 💎${nw}!`,
    cash: "🪙 Cash:",
    investments: "📈 Invest:",
    items: "🛒 Items:",
    skills: "Skills:",
    cashLegend: "Cash",
    investLegend: "Investments",
    itemsLegend: "Items",
    whatTaught: "💡 What this game taught:",
    lessons: [
      "Skills (preparation) compound your income over time",
      "Investments create passive income — money working for you",
      "Diversifying (cash + investments + items) builds resilience",
      "Work-Life Balance isn't optional — burnout costs more than it earns",
      "Life events are unpredictable — an emergency fund protects you",
    ],
    playAgain: "Play Again →",
  },
};

// ── Armenian ──────────────────────────────────────────────────────────────────
// Replace each English string below with its Armenian equivalent.
// Functions that take numbers/strings must keep the same signature.
const hy: typeof en = {
  lang: "🇦🇲 ՀՅ",
  setup: {
    tagline:
      "Կառուցիր կապիտալ, կայացրու խելացի որոշումներ և սովորիր ֆինանսներ` ամբողջ ընտանիքի համար",
    players: "👥 Խաղընկերներ (2–6)",
    playerPlaceholder: (n: number) => `Խաղընկեր ${n}`,
    addPlayer: "+ Ավելացնել խաղընկեր",
    gameLength: "⏱ Խաղի տևողությունը",
    gameLengths: [
      { label: "Արագ", time: "~20 րոպե" },
      { label: "Ստանդարտ", time: "~30 րոպե" },
      { label: "Երկար", time: "~45 րոպե" },
    ],
    rounds: "շրջափուլ",
    startGame: "Սկսել խաղը →",
    footer1:
      "Ստացիր գումար, ներդրումներ կատարիր և պատրաստվիր առաջ շարժվել։ Կապիտալը հաղթանակ կբերի։",
    footer2: "Հետևիր աշխատանք/կյանք բալանսին, որպեսզի չսպառվես։",
  },
  board: {
    newGame: "Նոր խաղ",
    players: "ԽԱՂԸՆԿԵՐՆԵՐ",
    rankings: "ԱՐԴՅՈՒՆՔ",
    round: "Շրջափուլ",
    currentTurn: "Քայլ է կատարում՝",
    costOfLiving: "Ապրուստի արժեքը՝",
    perTurn: "/ շրջափուլ",
  },
  phases: {
    DRAW_EVENT: "⚡ Անսպասելի իրադարձություն",
    CHOOSE_DECK: "🃏 Ընտրիր ուղին",
    REVEAL_CARD: "👁 Քարտը երևաց",
    INVEST: "📈 Ներդրումային գոտի",
    SPEND: "💸 Վճարիր ծախսերը",
    BUY_ITEMS: "🛒 Գնիր ապրանքներ",
    END_TURN: "⏭ Ավարտել",
    GAME_OVER: "🏆 Խաղն ավարտված է",
  },
  action: {
    timeForEvent: "Կյանքում անսպասելի իրադարձություն է տեղի ունեցել",
    eventDesc: "Քաշիր քարտը, որպեսզի տեսնես թե ինչ է պատահել",
    okContinue: "Լավ, շարունակել →",
    drawEventCard: "⚡ Քաշել քարտը",
    choosePath: "Ընտրիր ուղին",
    whatWillYouDo: "Ի՞նչ կուզես անես այս քայլին",
    earningsDeck: "💰 Փող աշխատել",
    earningsDesc:
      "Աշխատել հիմա։ Աշխատել ավելին, եթե ունես համապատասխան ունակություններ։",
    prepDeck: "📚 Հմտանալ",
    prepDesc: "Չաշխատել հիմա, հմտանալ ու շատացնել փողը հետո։",
    rest: "😴 Հանգստանալ",
    restDesc: "Ստանալ +2 էներգիա",
    nextInvest: "Հաջորդը՝ Ներդրումներ →",
    investments: "📈 Ներդրումներ",
    invest: "💼 Ներդրումներ",
    portfolio: "📊 Պորտֆել",
    skip: "⏭ Բաց թողնել",
    lowRisk: "🏦 Ցածր ռիսկ",
    highRisk: "🚀 Բարձր ռիսկ",
    lowRiskDesc: "Հաստատուն +5%/շրջափուլ. Եկամուտ՝ -10%-ից մինչև +25%",
    highRiskDesc: "Տատանվող +10%/շրջափուլ. Եկամուտ՝ -60% to +100%",
    amount: "Գումար",
    available: (n: number) => `Հասանելի՝ 🪙${n}`,
    investBtn: (n: number) => `Ներդնել 🪙${n} →`,
    investingIn: "Ներդնում է",
    inFund: (risk: "low" | "high" | "bond") =>
      risk === "bond"
        ? "Government Bond"
        : risk === "low"
          ? "Index Fund"
          : "Tech Startup",
    rollDice: "🎲 Քցիր զառը",
    rolling: "...",
    continueTo: "Շարունակել →",
    noInvestments: "Ներդրումներ չկան",
    doneInvestments: "Վերջացնել →",
    sell: "Վաճառել",
    paidNowRound: (paid: number, now: number, inc: number) =>
      `Գնվել է՝ 🪙${paid} · Հիմա՝ 🪙${now} · +${inc}/շրջափուլ`,
    burnoutTitle: "Ուժասպառ եղար",
    burnoutDesc:
      "Բաց ես թողնում այս քայլը, որպեսզի հանգստանաս։ Էներգիան շուտով կդառնա 5:",
    recoverPassTurn: "Հանգստանալ & բաց թողնել →",
    bondLabel: "🏛️ Պարտատոմս",
    bondDesc: "Ֆիքսված եկամուտ առանց ռիսկի",
    bondPortfolio: (n: number) =>
      `🏛️ Ֆիքսված · +${n}/շրջափուլ · վաճառվում է այս գնով`,
    perRound: "/շրջափուլ",
    yourTurn: "Քո քայլը!",
    diceJackpot: "Джекпот! 🎉", // TODO: translate to Armenian
    diceBigWin: "Մեծ հաղթանակ! 🚀", // TODO: translate to Armenian
    diceNiceGain: "Լավ շահում ✨", // TODO: translate to Armenian
    diceBreakEven: "Ոչ-ոքի 😐", // TODO: translate to Armenian
    diceSmallLoss: "Փոքր կորուստ 😬", // TODO: translate to Armenian
    diceBigLoss: "Ախ! Մեծ կորուստ 💸", // TODO: translate to Armenian
  },
  spend: {
    costOfLivingTitle: "💸 Ապրուստի արժեքը",
    costOfLivingNote: "Սա ավտոմատ վճարում ես, քանի որ գոյատևման գինն է։",
    yourBalance: (before: number, after: number) =>
      `Քո բալանսը՝ 🪙${before} → 🪙${Math.max(0, after)}`,
    payAndContinue: (n: number) => `Վճարել 🪙${n} և շարունակել →`,
    buyItemsTitle:
      "🛒 Ըստ ցանկության՝ գնիր իրեր, որպեսզի լավացնես կյանքդ ու ստանաս էներգիա",
    cantAfford: "Այս պահին ոչինչ չես կարող քեզ թույլ տալ",
    endTurn: "Ավարտել քայլը →",
    youOwn: "Դու ունես՝",
    wlbBonus: (n: number) => `+${n} էներգիա`,
    wlbBoostHint: "✨ Շատացրու էներգիադ", // TODO: translate
    sellBackTitle: "Քո իրերը, որոնք կարող ես նաև վաճառել", // TODO: translate
    sellingRemovesWlb: (n: number) => `Վաճառելը -${n} էներգիա է`, // TODO: translate
    sellFor: (n: number) => `Վաճառել 🪙${n}`,
    popularBadge: "🔥 Հայտնի", // TODO: translate to Armenian
    resell: (n: number) => `վերավաճառք 🪙${n}`, // TODO: translate to Armenian
  },
  // ── TODO: translate each item's name and description into Armenian ──
  items: {
    item_001: {
      name: "Ընտանեկան արձակուրդ",
      description: "Միասին ճամփորդելը թարմացնում և լիցքավորում է բոլորին։",
    },
    item_002: {
      name: "Հեծանիվ",
      description: "Օգնում է մարզավիճակդ պահել և խնայել տրանսպորտի վրա։",
    },
    item_003: {
      name: "Խաղային կոնսոլ",
      description: "Զվարճանք ամբողջ ընտանիքի համար։",
    },
    item_004: {
      name: "Հուսալի մեքենա",
      description:
        "Բացում է ավելի լավ աշխատանքի հնարավորություններ և նվազեցնում է ճանապարհի սթրեսը։",
    },
    item_005: {
      name: "Այգու պահեստատնակ",
      description: "Նախագիծ, որը նաև ավելացնում է տան արժեքը։",
    },
    item_006: {
      name: "Երաժշտական դասեր",
      description: "Ստեղծագործական զբաղմունք, որը նվազեցնում է սթրեսը։",
    },
    item_007: {
      name: "Արտակարգ ֆոնդի խնայատուփ",
      description:
        "Կողպված խնայողություններ՝ պաշտպանված անսպասելի իրադարձություններից։",
    },
    item_008: {
      name: "Մարզասարքեր",
      description: "Մարզանքը բարելավում է կենտրոնացումը և տրամադրությունը։",
    },
  } as Record<string, { name: string; description: string }>,
  card: {
    earningsLabel: "ԵԿԱՄՈՒՏ",
    prepLabel: "ՀՄՏԱՆԱԼ",
    eventLabel: "ԻՐԱԴԱՐՁՈՒԹՅՈՒՆ",
    boostedBy: "Ավելացված է՝",
    grants: "Դրամաշնորհներ՝",
    bonus: "բոնուս",
    affectsAll: "⚠️ Ազդում է բոլոր խաղընկերների վրա",
    halvespenalty: "կիսում է տույժը",
    wlbCost: (n: number) => `Էներգիա -${n}`,
    wlbChange: (n: number) => `Էներգիա ${n > 0 ? "+" : ""}${n}`,
  },
  icons: {
    degree: "🎓 Աստիճան",
    trade_cert: "🔧 Արհեստի վկայական",
    skill_badge: "💻 Հմտություն",
    network: "🤝 Շրջապատ",
    language: "🌍 Լեզու",
    leadership: "⭐ Առաջնորդություն",
  },
  // ── TODO: translate each event card's title / description / conceptExplanation into Armenian ──
  events: {
    event_001: {
      title: "Բժշկական միջամտություն",
      description: "Անսպասելի բժշկական միջամտության կարիք առաջացավ։",
      conceptExplanation:
        "Անվտանգության ֆոնդը հենց սրա համար է։ 3-6 ամսվա ծախսերի գումար ունենալը կպաշտպանի քեզ պարտքերի մեջ ընկնելուց, երբ կյանքը անակնկալներ է մատուցում։",
    },
    event_002: {
      title: "Տարեվերջյան բոնուս",
      description: "Ղեկավարը բարձր է գնահատել քո աշխատանքը։",
      conceptExplanation:
        "Բոնուսները եկամտի ոչ հաստատուն աղբյուր են։ Հիանալի է, երբ գալիս են, սակայն մի հենվիր դրանց վրա պարտադիր ծախսերի համար։ Խնայիր կամ ներդրում կատարիր բոնուսները, որպեսզի ավելի ապահովություն ու կապիտալ ձևավորես։",
    },
    event_003: {
      title: "Գլոբալ ճգնաժամ",
      description: "Մարկետներն անկում են ապրում։ Բոլորը զգում են դա։",
      conceptExplanation:
        "Ճգնաժամը ազդում է բոլոր ակտիվների վրա։ Կարևոր է մի քանի տեսակի ներդրում ունենալը, որպեսզի բալանս լինի։ Խնայողությունները կտան հանգստություն։",
    },
    event_004: {
      title: "Վճարովի արձակուրդ",
      description: "Հանգստացիր ընտանիքի հետ, վճարվում է։",
      conceptExplanation:
        "Վճարովի արձակուրդ տրամադրող երկրներն ու ընկերությունները օգնում են աշխատողներին պահպանել իրենց եկամուտը՝ առանց ընտանիքի հետ անցկացվող ժամանակը զոհաբերելու։ Աշխատանք ընդունելիս արտոնությունները նույնքան կարևոր են, որքան աշխատավարձը։",
    },
    event_005: {
      title: "Փչացել է մեքենան",
      description: "Ընթացագործի մոտ պետք է տանել։",
      conceptExplanation:
        "Մարման ֆոնդը գումար է, որը ամեն ամիս մի կողմ ես դնում կանխատեսելի, բայց ոչ կանոնավոր ծախսերի համար, օրինակ՝ մեքենայի վերանորոգման։ Այդպես նման ծախսերը չեն խառնվում քո բյուջեն։ Նախապես պլանավորիր նման ծախսերը՝ մինչև դրանք տեղի ունենան։",
    },
    event_006: {
      title: "Հարկային վերադարձ",
      description: "Պետությունը փող է ուղարկել։",
      conceptExplanation:
        "Հարկային վերադարձը նշանակում է, որ տարվա ընթացքում ավելի շատ հարկ ես վճարել, քան պետք էր։ Սա քո գումարն է, որը քեզ վերադարձվում է, ոչ թե անսպասելի շահույթ։ Խելացի հարկային պլանավորումը կարող է ամբողջ տարվա ընթացքում բարելավել քո դրամական հոսքը։",
    },
    event_007: {
      title: "Վարձակալությունը թանկացել է",
      description: "15%-ով թանկացել է քո տան վարձակալությունը։",
      conceptExplanation:
        "Երբ տուն ես վարձակալում, քո բնակարանի ծախսը որոշ չափով վերահսկում է մեկ ուրիշը։ Իսկ տուն ունենալու դեպքում կարող ես ավելի կայուն պահել ամսական վճարումները։ Վարձակալելու և գնելու տարբերությունը հասկանալը կարևոր ֆինանսական հմտություն է։",
    },
    event_008: {
      title: "Ցլի շուկա",
      description: "Բաժնետոմսերի գինը կտրուկ աճել է։",
      conceptExplanation:
        "Աճող շուկան պարգևատրում է այն ներդրողներին, ովքեր մնացել են շուկայում նույնիսկ անորոշ ժամանակներում։ Շուկայում երկար մնալը սովորաբար ավելի լավ է, քան ճիշտ պահը գուշակելու փորձերը։ Համբերությունը ներդրողի ամենամեծ ուժերից է։",
    },
    event_009: {
      title: "Աշխատանքից կրճատում",
      description:
        "Ընկերությունը կրճատումներ է արել։ Դու հայտնվել ես ցուցակում։",
      conceptExplanation:
        "Նույնիսկ աշխատասեր մարդիկ կարող են կրճատվել աշխատանքից։ 3–6 ամսվա ծախսերի չափով ապահովության ֆոնդ ունենալը քեզ ժամանակ է տալիս գտնելու հաջորդ լավ աշխատանքը, ոչ թե ընդունելու առաջին պատահած տարբերակը։",
    },
    event_010: {
      title: "Կողմնակի աշխատանքը ավելին է ետ տվել",
      description: "Քո կողմնակի աշխատանքը ավելի շատ գումար է բերել։",
      conceptExplanation:
        "Եկամտի մի քանի աղբյուր ունենալը լավ գաղափար է։ Եթե մեկ աղբյուրը դադարի, մյուսները կարող են շարունակել գումար բերել։ Կողմնակի աշխատանքը շուտ սկսելը, թեկուզ փոքր մասշտաբով, օգնում է ֆինանսապես ավելի դիմացկուն դառնալ։",
    },
    event_011: {
      title: "Չնախատեսված ծախս",
      description: "Լվացքի մեքենան փչացել է։",
      conceptExplanation:
        "Տուն ունենալը նշանակում է նաև սպասարկման և վերանորոգման ծախսեր։ Դրանք հաճախ տարեկան կազմում են տան արժեքի մոտ 1–2%-ը։ Եթե դրա համար նախապես գումար ես նախատեսում, անսպասելի խնդիրները չեն վերածվում ֆինանսական ճգնաժամի։",
    },
    event_012: {
      title: "Նվեր քո ծնունդին",
      description: "Ընտանիքը գումար է ուղարկել։",
      conceptExplanation:
        "Անսպասելի ստացված գումարը լավ հնարավորություն է ներդրում անելու, ոչ թե անմիջապես ծախսելու համար։ Նույնիսկ փոքր ներդրումը 10 տարվա ընթացքում կարող է զգալիորեն աճել՝ բարդ տոկոսի շնորհիվ։",
    },
    event_013: {
      title: "Մարկետի կորեկցիա",
      description: "Ներդրումները կորցրել են արժեքը 10%-ով",
      conceptExplanation:
        "Շուկայի կորեկցիաները նորմալ երևույթ են և ամեն տարի կարող են տեղի ունենալ։ Երկարաժամկետ ներդրողները խուճապի չեն մատնվում և չեն վաճառում ամեն ինչ։ Հենց նման անկումներին դիմանալով է ժամանակի ընթացքում հարստություն ստեղծվում։",
    },
    event_014: {
      title: "Կրթաթոշակ ես ստացել",
      description: "Հաղթել ես կրթաթոշակի մրցույթում։",
      conceptExplanation:
        "Կրթաթոշակներն ու դրամաշնորհները կրթության համար տրվող անվճար գումարներ են։ Ի տարբերություն ուսանողական վարկերի՝ դրանք վերադարձնելու կարիք չկա։ Միշտ արժե դիմել։",
    },
    event_015: {
      title: "Ապրուստը թանկացել է",
      description: "Ինֆլացիան բարձր է, ամեն ինչ ավելի թանկ է։",
      conceptExplanation:
        "Ինֆլացիան նվազեցնում է կանխիկ գումարի արժեքը։ Եթե գումարը ներդրված է այնպիսի ակտիվներում, որոնք ժամանակի ընթացքում աճում են ինֆլացիայից արագ, օրինակ՝ բաժնետոմսերում, դա օգնում է պաշտպանել քո փողի արժեքը։",
    },
    event_016: {
      title: "Ընկերության եկամտից գումար ես ստացել",
      description: "Քո ղեկավարը կիսում է հաջողությունը քեզ հետ",
      conceptExplanation:
        "Շահույթի բաշխումը և ընկերության բաժնեմասով վարձատրությունը ուժեղ արտոնություններ են, որոնք կարող են ժամանակի ընթացքում հարստություն ստեղծել։ Աշխատանքի առաջարկ գնահատելիս մի նայիր միայն աշխատավարձին․ կարևոր է ամբողջական փաթեթը։",
    },
    event_017: {
      title: "Ջրի խողովակը պայթել է",
      description: "Տանը քաոս է։",
      conceptExplanation:
        "Տուն ունենալը նշանակում է, որ վերանորոգման ծախսերը հիմնականում քո պատասխանատվությունն են։ Տան ապահովագրությունը կարող է ծածկել մեծ վնասները, բայց դու միևնույն է վճարումներ ես ունենալու։ Այդ պատճառով միշտ պետք է ունենալ տան արտակարգ ֆոնդ։",
    },
    event_018: {
      title: "Տոկոսադրույքները բարձրացել են",
      description:
        "Կենտրոնական բանկը բարձրացրել է տոկոսադրույքները։ Վարկ վերցնելը թանկացել է։",
      conceptExplanation:
        "Կենտրոնական բանկերը բարձրացնում են տոկոսադրույքները՝ ինֆլացիան դանդաղեցնելու համար։ Դա թանկացնում է վարկերն ու հիփոթեքները, բայց միաժամանակ խնայողական հաշիվները կարող են ավելի շատ տոկոս վճարել։ Ֆիքսված տոկոսադրույքով պարտքը կարող է քեզ պաշտպանել նման փոփոխություններից։",
    },
    event_019: {
      title: "Ընկերոջ հարսանիք",
      description: "Դու գնալու ես հարսանիք։ Խնջույքը թանկ է։",
      conceptExplanation:
        "Սոցիալական միջոցառումների վրա ծախսված գումարը նույնպես ծախս է։ Լավ ընկեր լինելը չի նշանակում պարտքերի մեջ մտնել։ Նորմալ է նման միջոցառումների համար բյուջե սահմանել և դրա մասին անկեղծ խոսել։",
    },
    event_020: {
      title: "Ինտերնետում գումար ես աշխատել",
      description: "Քո սարքած օնլայն խաղը լավ է գնացել։",
      conceptExplanation:
        "Ինտերնետային հաջողությունը կարող է իրական եկամուտ բերել՝ գովազդային համագործակցություններ, ապրանքների վաճառք կամ հրավերներ։ Բայց դա անկանխատեսելի է։ Դրա վրա պետք չէ հույս դնել որպես հիմնական եկամտի աղբյուր։",
    },
    event_021: {
      title: "Տեխնոլոգիական բում",
      description: "Ինովացիան առաջ է տանում տնտեսությունը։",
      conceptExplanation:
        "Տարբեր ոլորտներ տարբեր ժամանակներում առաջ են տանում տնտեսությունը։ Երբ տեխնոլոգիական ոլորտը աճում է, դրանում ներդրում կատարած մարդիկ շահում են։ Աճող ոլորտներում թեկուզ փոքր ներդրում ունենալը կարող է երկարաժամկետ հարստություն ստեղծել։",
    },
    event_022: {
      title: "Զեղծարարություն",
      description: "Ինչ-որ մեկն օգտագործել է քո բանկային տվյալները։",
      conceptExplanation:
        "Կիբերհանցագործությունը ամեն տարի միլիարդավոր դոլարների վնաս է պատճառում։ Քո թվային ինքնությունը պաշտպանելը՝ ուժեղ գաղտնաբառերով, երկքայլ հաստատմամբ և վարկային սահմանափակումներով, նույնքան կարևոր է, որքան տան դուռը կողպելը։",
    },
    event_023: {
      title: "Անվճար օնլայն ուսուցում",
      description: "Դու վերցրել ես անվճար մասնագիտացում։",
      conceptExplanation:
        "Coursera-ի, edX-ի և Khan Academy-ի նման անվճար ուսուցման հարթակները կրթությունն ավելի հասանելի են դարձրել։ Անվճար դասընթացը չի փոխարինում դիպլոմին, բայց կարող է ցույց տալ քո մոտիվացիան և օգնել նոր հմտություններ զարգացնել։",
    },
    event_024: {
      title: "Գազը թանկացել է",
      description: "Ձմեռը գազը թանկանում է։",
      conceptExplanation:
        "Կոմունալների նման ֆիքսված ծախսերը իրականում կարող են ավելի շատ տատանվել, քան մարդիկ սպասում են։ Տան էներգետիկ ստուգումը, լավ մեկուսացումը կամ արևային վահանակները ներդրումներ են, որոնք կարող են նվազեցնել ապագա կենցաղային ծախսերը։",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  // ── TODO: translate each earnings card's title / description / conceptExplanation into Armenian ──
  earnings: {
    earn_001: {
      title: "Ֆրիլանս վեբ նախագիծ",
      description: "Հաճախորդը քեզ վճարում է իր կայքը պատրաստելու համար։",
      conceptExplanation:
        "Ֆրիլանսը թույլ է տալիս քո հմտությունները ուղիղ վաճառել հաճախորդներին։ Եթե ունես պորտֆոլիո, դիպլոմ կամ հմտության վկայական, կարող ես ավելի բարձր գին պահանջել․ գործատուներն ու հաճախորդները ավելի շատ են վճարում ապացուցված փորձի համար։",
    },
    earn_002: {
      title: "Արտաժամյա հերթափոխ",
      description: "Քո ղեկավարը լրացուցիչ ժամերի համար աշխատող է փնտրում։",
      conceptExplanation:
        "Արտաժամյա աշխատանքը սովորաբար մեկ ժամի համար ավելի շատ է վճարվում, բայց նաև ավելի շատ էներգիա է խլում։ Երկար ժամանակ գերծանրաբեռնված աշխատելը կարող է բերել ուժասպառության։ Հետևիր աշխատանք-կյանք հավասարակշռությանը․ ավելի շատ գումարը միշտ չէ, որ արժե այդ գինը։",
    },
    earn_003: {
      title: "Մասնագիտական արհեստի աշխատանք",
      description: "Սանտեխնիկայի պայմանագիր է հայտնվել։",
      conceptExplanation:
        "Արհեստի վկայականները քեզ հնարավորություն են տալիս աշխատել պահանջված և լավ վարձատրվող ոլորտներում՝ առանց չորս տարվա համալսարանական կրթության։ Էլեկտրիկները, սանտեխնիկներն ու եռակցողները միշտ պահանջված են։",
    },
    earn_004: {
      title: "Թիմի ղեկավարի առաջխաղացում",
      description: "Քո առաջնորդական հմտությունները նկատվել են։",
      conceptExplanation:
        "Առաջնորդական հմտությունները հաճախ նույնքան արժեքավոր են, իսկ երբեմն նույնիսկ ավելի արժեքավոր, քան միայն տեխնիկական կարողությունները։ Ղեկավարման պատասխանատվություն ունեցող ավագ պաշտոնները սովորաբար ավելի բարձր աշխատավարձ և ավելի լավ արտոնություններ են ունենում։",
    },
    earn_005: {
      title: "Լեզվի անհատական դասավանդում",
      description: "Առցանց սովորեցրու քո երկրորդ լեզուն։",
      conceptExplanation:
        "Երկրորդ լեզու իմանալը դռներ է բացում միջազգային հաճախորդների և արտասահմանյան աշխատանքի հնարավորությունների համար։ Երկլեզու աշխատողները միջինում 10–15% ավելի շատ են վաստակում, քան միայն մեկ լեզու իմացողները։",
    },
    earn_006: {
      title: "Սկսնակ գրասենյակային աշխատանք",
      description: "Ամեն ամիս կայուն աշխատավարձ ես ստանում։",
      conceptExplanation:
        "Կայուն եկամուտը ֆինանսական կայունության հիմքն է։ Նույնիսկ համեստ, բայց կանոնավոր եկամուտը թույլ է տալիս բյուջե կազմել, խնայել և ժամանակի ընթացքում ներդրումներ անել։",
    },
    earn_007: {
      title: "Խորհրդատվական աշխատանք",
      description: "Ընկերությանը պետք է քո փորձը։",
      conceptExplanation:
        "Խորհրդատուները վճարվում են իրենց մասնագիտացված գիտելիքի համար։ Որքան հազվադեպ և արժեքավոր են քո հմտությունները, այնքան ավելի բարձր կարող ես գնահատել քո աշխատանքը։ Նման հնարավորությունները հաճախ գալիս են կապերի միջոցով։",
    },
    earn_008: {
      title: "Շուկայի տաղավարի տեր",
      description: "Տեղական շուկայում վաճառիր ձեռագործ իրեր։",
      conceptExplanation:
        "Կողմնակի աշխատանքը կարող է լրացնել քո հիմնական եկամուտը։ Փոքր բիզնեսները հաճախ հենց այսպես են սկսվում՝ նախ փորձարկելով գաղափարը, հետո միայն այն դարձնելով հիմնական աշխատանք։",
    },
    earn_009: {
      title: "Ծրագրավորողի աշխատանք",
      description:
        "Տեխնոլոգիական ընկերությունը քեզ ընդունում է մշտական աշխատանքի։",
      conceptExplanation:
        "Ծրագրավորողները աշխատաշուկայում ամենաբարձր վարձատրվողներից են։ Հմտության վկայականը կամ դիպլոմը զգալիորեն մեծացնում են նման աշխատանք ստանալու քո հնարավորությունները։",
    },
    earn_010: {
      title: "Առաքման վարորդ",
      description: "Ճկուն ժամեր և արագ վճարում։",
      conceptExplanation:
        "Այս աշխատանքը տալիս է ճկունություն, բայց սովորաբար չի տալիս արտոնություններ, օրինակ՝ առողջության ապահովագրություն կամ վճարովի արձակուրդ։ Կարճաժամկետ լավ տարբերակ է, բայց երկարաժամկետ պետք է հասկանալ՝ ինչից ես հրաժարվում դրա դիմաց։",
    },
    earn_011: {
      title: "Միջազգային վաճառքի գործարք",
      description: "Գործարք ես կնքում արտասահմանյան հաճախորդի հետ։",
      conceptExplanation:
        "Միջազգային բիզնեսից վերցված հմտությունները ավելի արժեքավոր են դառնում։ Ընկերությունները ավելի բարձր են վճարում այն մարդկանց, ովքեր կարողանում են հաղթահարել մշակութային և լեզվական տարբերությունները ու գործարքներ կնքել արտերկրում։",
    },
    earn_012: {
      title: "Խոտհնձի աշխատանք",
      description: "Օգնիր հարևաններին իրենց այգիների հարցում։",
      conceptExplanation:
        "Սա հիանալի առաջին աշխատանք է երեխաների համար։ Քո ժամանակը գումարի դիմաց փոխանակելը վաստակելու ամենապարզ ձևն է։ Իսկ երբ հմտություններդ զարգացնում ես, կարող ես ավելի բարձր գին պահանջել և ավելի խելացի աշխատել։",
    },
    earn_013: {
      title: "Շտապ կանչով էլեկտրիկ",
      description: "Շտապ էլեկտրական վերանորոգման աշխատանք։",
      conceptExplanation:
        "Շտապ կանչերով աշխատող արհեստավորները ավելի բարձր վարձատրություն են ստանում, քանի որ հասանելի են այն ժամանակ, երբ շատերը չեն աշխատում։ Արհեստի վկայականը և հասանելի լինելը միասին կարող են ուժեղ եկամտի աղբյուր դառնալ։",
    },
    earn_014: {
      title: "Առաջարկության բոնուս",
      description: "Դու խորհուրդ ես տվել լավ գործընկերոջ՝ և բոնուս ստացել։",
      conceptExplanation:
        "Ուժեղ կապերի ցանցը կարող է անսպասելի կերպով գումար բերել։ Շատ ընկերություններ առաջարկության բոնուս են տալիս, որովհետև վստահում են իրենց աշխատակիցներին լավ թեկնածուներ առաջարկելու հարցում։",
    },
    earn_015: {
      title: "Հետազոտական դրամաշնորհ",
      description: "Քո հետազոտական նախագծի համար ֆինանսավորում ես ստացել։",
      conceptExplanation:
        "Բարձրագույն կրթական աստիճանները բացում են հետազոտական ֆինանսավորման, դրամաշնորհների և կրթաթոշակների դռներ։ Սրանք եկամտի աղբյուրներ են, որոնք առանց համապատասխան որակավորման հաճախ հասանելի չեն լինում։",
    },
    earn_016: {
      title: "Երեխայի խնամք",
      description: "Խնամիր հարևանի երեխաներին։",
      conceptExplanation:
        "Պատասխանատվությունը վստահություն է ստեղծում, իսկ վստահությունը կարող է գումար բերել։ Հուսալի դայակները հաճախ ունենում են կրկնվող հաճախորդներ և առաջարկություններ՝ պարզ ծառայությունը դարձնելով փոքր բիզնես։",
    },
    earn_017: {
      title: "Փոդքասթի հովանավորություն",
      description: "Քո փոդքասթը վճարովի հովանավոր է գտել։",
      conceptExplanation:
        "Բովանդակություն ստեղծելը ժամանակակից եկամտի աղբյուր է։ Լսարան հավաքելը սկզբում ժամանակ է պահանջում, բայց երբ այն արդեն ունես, հովանավորները վճարում են քո լսարանին հասանելիություն ստանալու համար։",
    },
    earn_018: {
      title: "Անշարժ գույքի ցուցադրում",
      description: "Օգնիր ընտանիքին գտնել իրենց երազանքի տունը։",
      conceptExplanation:
        "Անշարժ գույքի գործակալները ստանում են միջնորդավճար՝ վաճառքի գնի որոշ տոկոսը։ Մեկ հաջող գործարքը կարող է հավասար լինել մի քանի ամսվա աշխատավարձի։ Միջնորդավճարով աշխատանքը կարող է շատ շահավետ լինել, բայց նաև անկանխատեսելի։",
    },
    earn_019: {
      title: "Ստոկ լուսանկարի վաճառք",
      description: "Քո լուսանկարները վաճառվել են ստոկ հարթակում։",
      conceptExplanation:
        "Ռոյալթի եկամուտը նշանակում է, որ ամեն անգամ, երբ ինչ-որ մեկն օգտագործում է քո ստեղծածը, դու գումար ես ստանում՝ առանց լրացուցիչ աշխատանքի։ Սա պասիվ եկամտի ամենաուժեղ ձևերից մեկն է։",
    },
    earn_020: {
      title: "Կրկնուսույցի դաս",
      description: "Օգնիր աշակերտին հանձնել քննությունը։",
      conceptExplanation:
        "Սովորեցնելը վաստակելու ամենահին ձևերից է։ Որքան որակավորված ես, այնքան ավելի բարձր կարող ես գին սահմանել։ Իսկ առցանց հարթակները թույլ են տալիս հասնել աշակերտների ամբողջ աշխարհում։",
    },
    earn_021: {
      title: "Քեյթերինգի աշխատանք",
      description: "Պատրաստիր և մատուցիր ուտելիք մասնավոր միջոցառման ժամանակ։",
      conceptExplanation:
        "Սննդի բիզնես սկսելու շեմը համեմատաբար ցածր է։ Քեյթերինգի կողմնակի աշխատանքը կարող է վերածվել լիարժեք բիզնեսի․ շատ ռեստորանների սեփականատերեր սկսել են ընկերների և միջոցառումների համար պատրաստելով։",
    },
    earn_022: {
      title: "Հավելվածի մշակման պայմանագիր",
      description: "Ստարտափը քեզ վարձում է իր MVP-ն պատրաստելու համար։",
      conceptExplanation:
        "Պայմանագրային ծրագրավորման աշխատանքը բարձր է վճարվում, որովհետև այն նախագծային է, ոչ թե ամսական աշխատավարձով։ Պորտֆոլիո ունեցող ծրագրավորողները կարող են մեկ օրվա համար ստանալ այնքան, որքան գրասենյակային աշխատանքում մի քանի շաբաթում։",
    },
    earn_023: {
      title: "Թարգմանչական աշխատանք",
      description: "Թարգմանիր բիզնես փաստաթուղթ։",
      conceptExplanation:
        "Մասնագիտական թարգմանիչները զգալիորեն ավելի շատ են վաստակում, քան ոչ պաշտոնական կամ պատահական թարգմանություն անողները։ Իրավական, բժշկական կամ տեխնիկական թարգմանության մեջ մասնագիտանալը թույլ է տալիս ավելի բարձր գին պահանջել։",
    },
    earn_024: {
      title: "Վինտաժ իրերի վերավաճառք",
      description: "Գնիր էժան գտածոներ և վաճառիր դրանք առցանց։",
      conceptExplanation:
        "Գնել էժան, վաճառել թանկ․ սա առևտրի հիմքն է։ Վերավաճառողները սովորում են նկատել թերագնահատված իրերը և վաճառել այնտեղ, որտեղ պահանջարկը բարձր է։ Այս հմտությունը նույն կերպ կիրառվում է բաժնետոմսերի, անշարժ գույքի և բիզնեսների դեպքում։",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  // ── TODO: translate each preparation card's title / description / conceptExplanation into Armenian ──
  preparation: {
    prep_001: {
      title: "Քոլեջի երկամյա կրթություն",
      description: "Երկու տարվա մասնագիտական ուսում։",
      conceptExplanation:
        "Երկամյա կրթությունը սովորաբար ավելի քիչ է արժենում, քան չորս տարվա համալսարանը, բայց միևնույն է կարող է բացել ավելի բարձր վարձատրվող աշխատանքների դռներ։ Ներդրման վերադարձը կարող է ավելի արագ լինել, քանի որ դու ավելի շուտ ես մտնում աշխատաշուկա և ավելի քիչ պարտք ես կուտակում։",
    },
    prep_002: {
      title: "Էլեկտրիկի վկայական",
      description: "Ավարտիր արհեստի ուսուցում փորձառու մասնագետի մոտ։",
      conceptExplanation:
        "Արհեստի վկայականները մեծ պահանջարկ ունեն։ Էլեկտրիկների միջին աշխատավարձը հաճախ ավելի բարձր է, քան շատ համալսարան ավարտածների եկամուտը՝ առանց ուսանողական պարտքի և ավելի արագ աշխատանքի անցնելու հնարավորությամբ։",
    },
    prep_003: {
      title: "Առցանց ծրագրավորման բութքեմփ",
      description: "12 շաբաթ ինտենսիվ ուսուցում։",
      conceptExplanation:
        "Բութքեմփերը արագ են և սովորաբար ավելի մատչելի, քան համալսարանական կրթությունը։ Դրանք տալիս են հմտության վկայական, բայց ոչ լիարժեք դիպլոմ․ որոշ աշխատանքների համար երկուսն էլ կարող են պետք գալ։ Սա կարիերան արագ փոխելու լավ տարբերակ է։",
    },
    prep_004: {
      title: "Նեթվորքինգի միջոցառում",
      description: "Ծանոթացիր քո ոլորտի մարդկանց հետ։",
      conceptExplanation:
        "Ուժեղ մասնագիտական կապերը կարող են մեծացնել քո եկամտի հնարավորությունները։ Շատ աշխատանքներ գտնվում են ոչ թե հայտարարությունների կայքերով, այլ ծանոթությունների ու կապերի միջոցով։ Ովքեր քեզ ճանաչում են, երբեմն նույնքան կարևոր է, որքան այն, ինչ դու գիտես։",
    },
    prep_005: {
      title: "Իսպաներենի դասընթաց",
      description: "Սովորիր խոսել երկրորդ լեզվով։",
      conceptExplanation:
        "Երկլեզու աշխատողները միջինում 10–15% ավելի շատ են վաստակում։ Իսպաներեն իմանալը դռներ է բացում Լատինական Ամերիկայում, ԱՄՆ-ում և Եվրոպայում․ սա շատ արժեքավոր կարիերային հմտություն է։",
    },
    prep_006: {
      title: "Առաջնորդության վորքշոփ",
      description: "Զարգացրու կառավարման և թիմային հմտություններդ։",
      conceptExplanation:
        "Առաջնորդական հմտությունները գործատուների կողմից ամենաբարձր գնահատվող հմտություններից են։ Շատ ոլորտներում մենեջերներն ու թիմի ղեկավարները զգալիորեն ավելի շատ են վաստակում, քան միայն անհատական աշխատանք կատարող մասնագետները։",
    },
    prep_007: {
      title: "Համալսարանական կրթություն",
      description: "Չորս տարի բարձրագույն կրթություն։",
      conceptExplanation:
        "Չորս տարվա համալսարանական կրթությունը բացում է մասնագիտական աշխատանքի, մագիստրատուրայի կամ հետազոտական կարիերայի դռներ։ Երկարաժամկետ եկամտի աճը կարող է զգալի լինել, բայց սկզբնական ծախսն էլ է մեծ։",
    },
    prep_008: {
      title: "Սանտեխնիկի աշակերտություն",
      description: "Սովորիր աշխատանքի ընթացքում փորձառու սանտեխնիկից։",
      conceptExplanation:
        "Աշակերտությունը թույլ է տալիս սովորել և միաժամանակ գումար վաստակել՝ առանց ուսանողական պարտքի։ Սանտեխնիկի աշխատանքը կայուն պահանջարկ ունի, քանի որ խողովակները միշտ վերանորոգման կարիք են ունենում։",
    },
    prep_009: {
      title: "Դիզայնի հմտությունների դասընթաց",
      description: "Սովորիր գրաֆիկ դիզայնի հիմունքները։",
      conceptExplanation:
        "Դիզայնի նման ստեղծագործական հմտությունները պահանջված են գրեթե բոլոր ոլորտներում։ Դիզայնի հմտության վկայականը կարող է համադրվել այլ որակավորումների հետ և բացել ինչպես ֆրիլանս, այնպես էլ մշտական աշխատանքի հնարավորություններ։",
    },
    prep_010: {
      title: "Ոլորտային կոնֆերանս",
      description: "Մասնակցիր և ելույթ ունեցիր քո ոլորտի մեծ միջոցառմանը։",
      conceptExplanation:
        "Կոնֆերանսները համատեղում են սովորելն ու կապեր հաստատելը։ Եթե այնտեղ ելույթ ես ունենում, քեզ սկսում են ընկալել որպես մասնագետ, և դա կարող է բերել նոր աշխատանքի, հաճախորդների կամ համագործակցության հնարավորություններ։",
    },
    prep_011: {
      title: "Ֆրանսերենի խորացված ծրագիր",
      description: "Երեք ամիս արտերկրում՝ Փարիզում։",
      conceptExplanation:
        "Լեզվական միջավայրում սովորելը սահուն խոսելու ամենաարագ ձևերից է։ Ֆրանսերեն իմանալը դռներ է բացում Եվրոպայում, Կանադայում և ֆրանսախոս Աֆրիկայում՝ մեծ տնտեսական հնարավորություններ ունեցող շուկաներում։",
    },
    prep_012: {
      title: "Տվյալագիտության վկայական",
      description: "Սովորիր վերլուծել և հասկանալ տվյալները։",
      conceptExplanation:
        "Տվյալների հետ աշխատելու հմտությունը գործատուների կողմից ամենաարագ աճող պահանջներից է։ Տվյալագիտության վկայականը կարող է բարձրացնել քո արժեքը գրեթե ցանկացած ոլորտում և օգնել ավելի բարձր վարձատրվող աշխատանքի հասնել։",
    },
  } as Record<
    string,
    { title: string; description: string; conceptExplanation: string }
  >,
  modal: {
    financeTip: "💡 Ֆինանսական գիտելիք",
    gotIt: "Հասկացա, շարունակել →",
    burnoutTitle: "Ուժասպառ ես!",
    burnoutMsg: (name: string) =>
      `${name}ը աշխատեց շատ և ուժասպառ եղավ։ Նա բաց կթողնի իր մյուս քայլը և էներգիա կհավաքի։`,
    burnoutLesson:
      "Ուժասպառ լինելը մեզ սովորեցնում է, որ հանգիստը կամավոր չէ․ այն նաև ֆինանսական ռազմավարություն է։ Ուժասպառությունը վերջում ավելի թանկ է նստում, քան այն եկամուտը, որը ստացել ես գերծանրաբեռնված աշխատելով։",
    understood: "Հասկացա →",
  },
  player: {
    wlb: "Էներգիա",
    netWorth: "Կապիտալ",
    burnoutBadge: "💤 Ուժասպառ",
    investments: (n: number) => `📈 ${n} ներդրում${n > 1 ? "ներ" : ""}`,
    passiveIncome: (n: number) => `+${n}🪙/շրջափուլ`,
  },
  endgame: {
    gameOver: "Խաղի ավարտ",
    wins: (name: string, nw: number) => `${name}ը հաղթում է 💎${nw}ով!`,
    cash: "🪙 Փող՝",
    investments: "📈 Ներդրում՝",
    items: "🛒 Իրեր՝",
    skills: "Հմտություններ՝",
    cashLegend: "Փող",
    investLegend: "Ներդրումներ",
    itemsLegend: "Իրեր",
    whatTaught: "💡 Ի՞նչ սովորեցրեց այս խաղը",
    lessons: [
      "Հմտություններն ու պատրաստված լինելը ժամանակի ընթացքում մեծացնում են քո եկամուտը",
      "Ներդրումները ստեղծում են պասիվ եկամուտ․ գումարը աշխատում է քո փոխարեն",
      "Դիվերսիֆիկացումը՝ կանխիկ գումար + ներդրումներ + իրեր, ստեղծում է ֆինանսական դիմացկունություն",
      "Աշխատանք-կյանք հավասարակշռությունը կամավոր չէ․ ուժասպառ լինելը թանկ է",
      "Կյանքի իրադարձությունները անկանխատեսելի են․ արտակարգ ֆոնդը պաշտպանում է քեզ",
    ],
    playAgain: "Խաղալ կրկին →",
  },
};

export const translations = { en, hy } as const;
export type Translations = typeof en;
